import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LearningObject } from '../Models/content-types/learning_object';
import EducationalObjective from '../Components/EducationalObjective';
import Overview from '../Components/Overview';
import Body from '../Components/Body';
import Footer from '../Components/Footer';
import ContainerPreview from '../Components/Container';
import { getKontent } from '../API/KontentAPI';
import { storeJWTToken, getJWTFromCookie } from '../Utilities/OIDCUtils';

/**
 * A React functional component that fetches and displays a learning object
 * based on a URL slug. This component utilizes React Bootstrap for layout
 * and styling, and integrates with a delivery client to fetch data.
 *
 * Features:
 * - Fetches a learning object from a delivery API using the provided slug.
 * - Displays the title, educational objective, overview, and body of the learning object.
 * - Uses React Bootstrap's `Container` and grid system for responsive layout.
 *
 * Dependencies:
 * - React Bootstrap for layout and styling.
 * - `useIntl` for internationalization.
 * - `useParams` for extracting route parameters.
 * - `useMemo` and `useEffect` for managing side effects and memoization.
 *
 * @returns A JSX element displaying the learning object content.
 */

const LearningObjects: React.FC = () => {
  const [learningObject, setlearningObject] = useState<LearningObject | null>(
    null,
  );

  const { learningObjectSlug } = useParams();

  const [jwt, setJWT] = useState(getJWTFromCookie());
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // only accept messages from specific domains
      if (
        ![
          'https://learningtest.theinstitutes.org',
          'https://learning.theinstitutes.org',
          'https://courses-test.theinstitutes.org',
          'https://courses.theinstitutes.org',
        ].includes(event.origin)
      ) {
        return;
      }
      try {
        if (event.data && event.data.type === 'jwt_auth' && event.data.token) {
          storeJWTToken(event.data.token);
          setJWT(event.data.token);
        }
      } catch (e) {
        console.error('Error saving JWT', e);
      }
    };

    window.addEventListener('message', handleMessage);

    // Remove the listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const fetchlearningObjects = async (): Promise<void> => {
      if (!learningObjectSlug) {
        console.error('learningObject slug is undefined');
        return;
      }
      if (learningObjectSlug) {
        try {
          const response = await getKontent(
            'elements.url_slug',
            learningObjectSlug,
          );
          if (response.data.items.length === 0) {
            console.error('No learningObject found for the given slug');
            return;
          }

          const learningObject = response.data.items[0] as LearningObject;

          setlearningObject(learningObject);
        } catch (error) {
          console.error('Error fetching learningObject:', error);
        }
      }
    };
    fetchlearningObjects();
  }, [learningObjectSlug, jwt]);

  const sendHeightToParent = () => {
    const LoRootElement = document.getElementById('root');
    let scrollHeight = 0;
    if (LoRootElement) {
      scrollHeight = LoRootElement.scrollHeight; // scrollHeight more accurate than computed height
    }
    const height = scrollHeight + 140; // add extra space for scrollbars and some misc space at the bottom

    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'resize', height: height }, '*');
    }
  };

  useLayoutEffect(() => {
    // Send height to parent window once all components are rendered
    if (learningObject) {
      // Use requestAnimationFrame to ensure DOM is fully painted
      requestAnimationFrame(() => {
        setContentLoaded(true);
        setTimeout(sendHeightToParent, 100); // Initial height send
      });
    }
  }, [learningObject]);

  useEffect(() => {
    // Use ResizeObserver to monitor height changes of the root container
    // This will detect accordion openings, dynamic content changes, etc.
    if (jwt && contentLoaded && window.parent && window.parent !== window) {
      const rootElement = document.getElementById('root');

      if (rootElement) {
        let animationFrameId: number | null = null;
        let lastHeight = 0;
        let isUpdatePending = false;

        const resizeObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            const { height } = entry.contentRect;

            // Only proceed if height has actually changed and no update is pending
            if (height > 0 && height !== lastHeight && !isUpdatePending) {
              lastHeight = height;
              isUpdatePending = true;

              // Cancel any pending animation frame
              if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
              }

              // Use requestAnimationFrame for browser-synchronized timing
              animationFrameId = requestAnimationFrame(() => {
                // Wait for multiple animation frames to ensure animations complete
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    // Double-check that height is still stable after animation frames
                    const currentHeight =
                      rootElement.getBoundingClientRect().height;
                    if (Math.abs(currentHeight - height) < 5) {
                      // Allow for small differences
                      sendHeightToParent();
                    }
                    isUpdatePending = false;
                    animationFrameId = null;
                  });
                });
              });
            }
          }
        });

        resizeObserver.observe(rootElement);

        // Clean up the observer and any pending animation frames
        return () => {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
          resizeObserver.disconnect();
        };
      }
    }
  }, [jwt, contentLoaded]);

  useEffect(() => {
    // Create an Intersection Observer to watch for the copyright container
    // Only start observing after content is loaded and rendered
    if (jwt && contentLoaded && window.parent && window.parent !== window) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            // If the element is intersecting (visible)
            if (entry.isIntersecting) {
              // Send message to parent window that copyright is visible
              if (window.parent && window.parent !== window) {
                window.parent.postMessage(
                  { type: 'completed', value: true },
                  '*',
                );
              }
              // Disconnect the observer after it's been seen once
              observer.disconnect();
            }
          });
        },
        {
          // Configure the observer options (0 means any amount of visibility triggers the callback)
          threshold: 0,
        },
      );

      // Start observing immediately since content is already loaded
      const copyrightElement = document.getElementById('summary');
      if (copyrightElement) {
        observer.observe(copyrightElement);
      }

      // Clean up the observer if the component unmounts
      return () => observer.disconnect();
    }
  }, [jwt, contentLoaded]);

  return (
    <>
      {window.parent && window.parent !== window ? (
        <div className="container-content" id="learning-object-container">
          <div className="page-header">
            {learningObject?.elements.title?.value}
          </div>

          <div className="row">
            <div className="col-md-12">
              {learningObject && (
                <>
                  {learningObject.elements.educational_objective && (
                    <EducationalObjective
                      element={learningObject.elements.educational_objective}
                    />
                  )}
                  {learningObject.elements.overview && (
                    <Overview element={learningObject.elements.overview} />
                  )}
                  {learningObject.elements.body && (
                    <Body element={learningObject.elements.body} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <ContainerPreview>
          <div className="page-header">
            {learningObject?.elements.title?.value}
          </div>

          <div className="row">
            <div className="col-md-12">
              {learningObject && (
                <>
                  {learningObject.elements.educational_objective && (
                    <EducationalObjective
                      element={learningObject.elements.educational_objective}
                    />
                  )}
                  {learningObject.elements.overview && (
                    <Overview element={learningObject.elements.overview} />
                  )}
                  {learningObject.elements.body && (
                    <Body element={learningObject.elements.body} />
                  )}
                </>
              )}
            </div>
          </div>
        </ContainerPreview>
      )}
      <Footer></Footer>
    </>
  );
};

export default LearningObjects;
